import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT_INFO, SERVICE_KEYS } from "../lib/data";

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="bg-[#0F172A] text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full overflow-hidden bg-white">
                <img src={CONTACT_INFO.logo} alt="SHK" className="h-full w-full object-cover" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-black text-white text-base">SHK HAUSTECHNIK</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">GmbH · Allschwil</div>
              </div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">{t.footer.tagline}</p>
            <div className="inline-flex items-center gap-2 rounded-full bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-300 border border-rose-500/30">
              <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
              {t.footer.pikett}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-display font-bold text-white text-sm uppercase tracking-wider mb-4">
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-2 text-sm">
              {[
                { to: "/", label: t.nav.home },
                { to: "/dienstleistungen", label: t.nav.services },
                { to: "/referenzen", label: t.nav.references },
                { to: "/ueber-uns", label: t.nav.about },
                { to: "/kontakt", label: t.nav.contact },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace("/", "") || "home"}`}
                    className="hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display font-bold text-white text-sm uppercase tracking-wider mb-4">
              {t.footer.ourServices}
            </h4>
            <ul className="space-y-2 text-sm">
              {SERVICE_KEYS.map((k) => (
                <li key={k}>
                  <Link
                    to={`/dienstleistungen/${k}`}
                    data-testid={`footer-service-${k}`}
                    className="hover:text-white transition-colors"
                  >
                    {t.services[k].name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-bold text-white text-sm uppercase tracking-wider mb-4">
              {t.footer.contactInfo}
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <span>
                  {CONTACT_INFO.street}<br />{CONTACT_INFO.city}
                </span>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT_INFO.phoneClean}`}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Phone className="h-4 w-4 text-sky-400" />
                  {CONTACT_INFO.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  data-testid="footer-email"
                  className="flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Mail className="h-4 w-4 text-sky-400" />
                  {CONTACT_INFO.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <span className="whitespace-pre-line text-slate-400">{t.contact.hoursText}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
            <p>© {new Date().getFullYear()} SHK Haustechnik GmbH. {t.footer.rights}</p>
            <span className="hidden sm:inline text-slate-700">·</span>
            <p className="flex items-center gap-1.5">
              <span>Erstellt von</span>
              <a
                href="https://alpenpro.agency/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-alpenpro-link"
                className="alpen-glow inline-block"
              >
                Alpen Pro Agency
              </a>
            </p>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/kontakt" className="hover:text-white">{t.footer.imprint}</Link>
            <Link to="/kontakt" className="hover:text-white">{t.footer.privacy}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
